import { useCallback } from "react";
import { useLocalStorage } from "./useLocalStorage";

export const getWatchKey = (mr) =>
	mr?.id ||
	`${mr?.project?.fullPath || mr?.projectPath || "unknown"}!${mr?.iid}`;

const makeSnapshot = (mr) => ({
	updatedAt: mr.updatedAt,
	userNotesCount: mr.userNotesCount ?? 0,
	discussions: mr.discussions ?? 0,
	state: mr.state,
	// Keep a copy so a watched MR still renders if it drops out of the
	// fetched lists (e.g. merged long ago, beyond the closed window).
	mr,
});

/**
 * Tracks the set of merge requests the user is watching plus a per-MR
 * "last seen" snapshot. Activity (new comments, new commits/updates, state
 * changes) is derived by diffing the live MR against its snapshot.
 */
export const useWatchedMRs = () => {
	const [watchedIds, setWatchedIds] = useLocalStorage("watched-mr-ids", []);
	const [snapshots, setSnapshots] = useLocalStorage("watched-mr-snapshots", {});

	const isWatched = useCallback(
		(mr) => watchedIds.includes(getWatchKey(mr)),
		[watchedIds],
	);

	const watchMR = useCallback(
		(mr) => {
			const key = getWatchKey(mr);
			setWatchedIds((prev) => (prev.includes(key) ? prev : [...prev, key]));
			setSnapshots((prev) => ({ ...prev, [key]: makeSnapshot(mr) }));
		},
		[setWatchedIds, setSnapshots],
	);

	const unwatchMR = useCallback(
		(mr) => {
			const key = getWatchKey(mr);
			setWatchedIds((prev) => prev.filter((id) => id !== key));
			setSnapshots((prev) => {
				if (!(key in prev)) return prev;
				const next = { ...prev };
				delete next[key];
				return next;
			});
		},
		[setWatchedIds, setSnapshots],
	);

	const toggleWatch = useCallback(
		(mr) => (isWatched(mr) ? unwatchMR(mr) : watchMR(mr)),
		[isWatched, unwatchMR, watchMR],
	);

	// Reset the snapshot to the MR's current state, clearing its activity badge.
	const markSeen = useCallback(
		(mr) => {
			const key = getWatchKey(mr);
			setSnapshots((prev) =>
				key in prev ? { ...prev, [key]: makeSnapshot(mr) } : prev,
			);
		},
		[setSnapshots],
	);

	const markAllSeen = useCallback(
		(mrs) => {
			setSnapshots((prev) => {
				let next = prev;

				for (const mr of mrs) {
					const key = getWatchKey(mr);
					if (!(key in prev)) continue;

					if (next === prev) next = { ...prev };
					next[key] = makeSnapshot(mr);
				}

				return next;
			});
		},
		[setSnapshots],
	);

	// Returns an array of change descriptors, or null when nothing is new.
	const getActivity = useCallback(
		(mr) => {
			const snap = snapshots[getWatchKey(mr)];
			if (!snap) return null;

			const changes = [];
			const newNotes = (mr.userNotesCount ?? 0) - (snap.userNotesCount ?? 0);
			if (newNotes > 0) changes.push({ type: "comments", count: newNotes });

			if (mr.state !== snap.state) {
				changes.push({ type: "state", from: snap.state, to: mr.state });
			} else if (
				newNotes <= 0 &&
				snap.updatedAt &&
				new Date(mr.updatedAt) > new Date(snap.updatedAt)
			) {
				changes.push({ type: "updated" });
			}

			return changes.length ? changes : null;
		},
		[snapshots],
	);

	return {
		watchedIds,
		snapshots,
		isWatched,
		watchMR,
		unwatchMR,
		toggleWatch,
		markSeen,
		markAllSeen,
		getActivity,
	};
};
